import React, { createContext, useCallback, useEffect, useReducer } from 'react'
import jwtDecode from 'jwt-decode'
import axiosPublic, { axiosPrivate } from '../../axios'
import MatxLoading from '../components/MatxLoading/MatxLoading'

const initialState = {
	isAuthenticated: false,
	isInitialised: false,
	signinUser: null,
	tokenPayload: null
}

const isValidToken = (accessToken) => {
	if (!accessToken) {
		return false
	}

	const decodedToken = jwtDecode(accessToken)
	const currentTime = Date.now() / 1000
	return decodedToken.exp > currentTime
}

const getTokenPayload = (accessToken) => {
	if (!isValidToken(accessToken)) {
		return {}
	}

	const decodedToken = jwtDecode(accessToken)
	const { user } = decodedToken
	const { id, email } = user

	return { id, email }
}

const createSigninUser = ({ id, email, role, firstName, lastName, avatar } = {}) => {
	return {
		id,
		email,
		role,
		firstName,
		lastName,
		avatar,
		name: `${firstName || ''} ${lastName || ''}`
	}
}

const userEqual = (userA, userB) => {
	return (
		userA?.id === userB?.id &&
		userA?.email === userB?.email &&
		userA?.role === userB?.role &&
		userA?.firstName === userB?.firstName &&
		userA?.lastName === userB?.lastName &&
		userA?.avatar === userB?.avatar &&
		userA?.name === userB?.name
	)
}

const setSession = (accessToken) => {
	if (accessToken) {
		localStorage.setItem('accessToken', accessToken)
		// axiosPublic.defaults.headers.common.Authorization = `Bearer ${accessToken}`
	} else {
		localStorage.removeItem('accessToken')
		// delete axiosPublic.defaults.headers.common.Authorization
	}
}

const reducer = (state, action) => {
	switch (action.type) {
		case 'INIT': {
			const { isAuthenticated, tokenPayload, signinUser } = action.payload

			return {
				...state,
				isAuthenticated,
				isInitialised: true,
				tokenPayload,
				signinUser
			}
		}
		case 'LOGIN': {
			const { tokenPayload, signinUser } = action.payload

			return {
				...state,
				isAuthenticated: true,
				tokenPayload,
				signinUser
			}
		}
		case 'LOGOUT': {
			return {
				...state,
				isAuthenticated: false,
				tokenPayload: null,
				signinUser: null
			}
		}
		case 'REGISTER': {
			const { signinUser } = action.payload

			return {
				...state,
				isAuthenticated: true,
				signinUser
			}
		}
		case 'RELOAD_SIGNIN_USER': {
			const { signinUser } = action.payload
			const newSigninUser = createSigninUser(signinUser)
			const oldSigninUser = state.signinUser

			const isUserEqual = userEqual(newSigninUser, oldSigninUser)
			if (isUserEqual) {
				return state
			}

			return {
				...state,
				signinUser: createSigninUser(signinUser)
			}
		}
		default: {
			return { ...state }
		}
	}
}

const AuthContext = createContext({
	...initialState,
	method: 'JWT',
	login: () => Promise.resolve(),
	logout: () => {},
	register: () => Promise.resolve(),
	reloadSigninUser: () => Promise.resolve()
})

export const AuthProvider = ({ children }) => {
	const [state, dispatch] = useReducer(reducer, initialState)

	const login = async (email, password) => {
		const response = await axiosPublic.post('/auth/signin', {
			email,
			password
		})

		const { accessToken } = response.data

		setSession(accessToken)

		const profileResponse = await axiosPrivate.get('/profile/light')
		const { data: signinUser } = profileResponse

		dispatch({
			type: 'LOGIN',
			payload: {
				tokenPayload: getTokenPayload(accessToken),
				signinUser: createSigninUser(signinUser)
			}
		})
	}

	const register = async (email, username, password) => {
		const response = await axiosPublic.post('/auth/register', {
			email,
			username,
			password
		})

		const { accessToken, user } = response.data

		setSession(accessToken)

		dispatch({
			type: 'REGISTER',
			payload: {
				user
			}
		})
	}

	const logout = () => {
		setSession(null)
		dispatch({ type: 'LOGOUT' })
	}

	const reloadSigninUser = useCallback(async () => {
		try {
			const res = await axiosPrivate.get('/profile/light')
			dispatch({
				type: 'RELOAD_SIGNIN_USER',
				payload: {
					signinUser: createSigninUser(res?.data)
				}
			})
		} catch (err) {
			if (err.status === 404) {
				logout()
			}
			// for other errors just keep the current user to prevent logout. E.g. temporary server outage.
		}
	}, [])

	useEffect(() => {
		;(async () => {
			try {
				const accessToken = window.localStorage.getItem('accessToken')

				if (accessToken && isValidToken(accessToken)) {
					setSession(accessToken)

					const profileResponse = await axiosPrivate.get('/profile/light')
					const { data: signinUser } = profileResponse

					dispatch({
						type: 'INIT',
						payload: {
							isAuthenticated: true,
							tokenPayload: getTokenPayload(accessToken),
							signinUser: createSigninUser(signinUser)
						}
					})
				} else {
					dispatch({
						type: 'INIT',
						payload: {
							isAuthenticated: false,
							tokenPayload: null,
							signinUser: null
						}
					})
				}
			} catch (err) {
				console.error(err)
				dispatch({
					type: 'INIT',
					payload: {
						isAuthenticated: false,
						tokenPayload: null,
						signinUser: null
					}
				})
			}
		})()
	}, [])

	if (!state.isInitialised) {
		return <MatxLoading />
	}

	return (
		<AuthContext.Provider
			value={{
				...state,
				method: 'JWT',
				login,
				logout,
				register,
				reloadSigninUser
			}}
		>
			{children}
		</AuthContext.Provider>
	)
}

export default AuthContext
